import React, { useState, useRef } from "react";
import { Dropdown, Button, Icon, Segment, Label, Header } from "semantic-ui-react";
import DatePicker from "react-datepicker";
import { subDays } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";

// A RID picker that searches the tagged-radio-ID database as you type and
// populates matches (alias + number). Selected values are stored as rid number
// strings; allowAdditions still lets you type a raw RID the DB hasn't tagged.
function RidSelect({ value, onChange }) {
  const asOption = (v) => ({ key: String(v), value: String(v), text: String(v) });
  const [options, setOptions] = useState(() => (value || []).map(asOption));
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const runSearch = (q) => {
    const term = (q || "").trim();
    if (!term) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(process.env.REACT_APP_BACKEND_SERVER + "/radio_ids/search?q=" + encodeURIComponent(term))
      .then((r) => r.json())
      .then((data) => {
        const found = (data.results || []).map((r) => ({
          key: String(r.rid),
          value: String(r.rid),
          text: (r.alias && r.alias !== "N/A") ? (r.alias + " (" + r.rid + ")") : String(r.rid),
        }));
        // Keep already-selected values visible even if they aren't in the latest
        // search results, or their labels would disappear.
        (value || []).forEach((v) => {
          if (!found.find((o) => o.value === String(v))) {
            found.push(asOption(v));
          }
        });
        setOptions(found);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const handleSearchChange = (e, { searchQuery }) => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => runSearch(searchQuery), 250);
  };

  return (
    <Dropdown
      placeholder="Search RID or unit alias…"
      fluid multiple search selection allowAdditions
      options={options}
      value={value}
      loading={loading}
      noResultsMessage="Type to search radio IDs"
      onSearchChange={handleSearchChange}
      onAddItem={(e, { value: v }) => setOptions((o) => [asOption(v), ...o])}
      onChange={(e, { value: v }) => onChange(v)}
    />
  );
}

// The stacked, SIEM-style condition builder. Each row is one condition; rows
// join to the row above them with AND or OR. Within a row, several values are
// OR'd together ("is any of"). Precedence follows boolean algebra - AND binds
// tighter than OR - and the matching backend compiler (backend/filter.js) uses
// the same rule, so what you see here is what gets queried.
//
// A row is: { field, op, values, join }
//   field: rid | talkgroup | group | starred | emergency
//   op:    in ("is any of" / "is") | notIn ("is none of" / "is not")
//   values: array (ignored for the boolean fields starred/emergency)
//   join:  AND | OR  (how it combines with the row above; first row ignores it)

const FIELD_OPTIONS = [
  { key: "rid", value: "rid", text: "Radio ID / Unit" },
  { key: "talkgroup", value: "talkgroup", text: "Talkgroup" },
  { key: "group", value: "group", text: "Group" },
  { key: "starred", value: "starred", text: "Starred" },
  { key: "emergency", value: "emergency", text: "Emergency" },
];

const OP_OPTIONS_SET = [
  { key: "in", value: "in", text: "is any of" },
  { key: "notIn", value: "notIn", text: "is none of" },
];

const OP_OPTIONS_BOOL = [
  { key: "in", value: "in", text: "is" },
  { key: "notIn", value: "notIn", text: "is not" },
];

const JOIN_OPTIONS = [
  { key: "AND", value: "AND", text: "AND" },
  { key: "OR", value: "OR", text: "OR" },
];

const BOOLEAN_FIELDS = ["starred", "emergency"];

export const newRow = () => ({ field: "talkgroup", op: "in", values: [], join: "AND" });

function AdvancedFilter({ rows, onChange, timeframe, onTimeframeChange, talkgroupList, groupList }) {

  const tf = timeframe || { from: null, to: null };
  const archiveDays = parseInt(process.env.REACT_APP_ARCHIVE_DAYS, 10) || 30;
  const minDate = subDays(new Date(), archiveDays);

  const updateRow = (index, changes) => {
    const next = rows.map((row, i) => (i === index ? { ...row, ...changes } : row));
    onChange(next);
  };

  const removeRow = (index) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const addRow = () => {
    onChange([...rows, newRow()]);
  };

  const handleFieldChange = (index, value) => {
    // Value types differ per field, so reset them when the field changes.
    updateRow(index, { field: value, values: [] });
  };

  const renderValueControl = (row, index) => {
    if (BOOLEAN_FIELDS.includes(row.field)) {
      // starred / emergency carry no value - the op ("is" / "is not") says it all.
      return null;
    }

    if (row.field === "talkgroup") {
      return (
        <Dropdown
          placeholder="Select talkgroups"
          fluid multiple search selection
          options={talkgroupList}
          value={row.values}
          onChange={(e, { value }) => updateRow(index, { values: value })}
        />
      );
    }

    if (row.field === "group") {
      return (
        <Dropdown
          placeholder="Select groups"
          fluid multiple search selection
          options={groupList}
          value={row.values}
          onChange={(e, { value }) => updateRow(index, { values: value })}
        />
      );
    }

    // rid: searchable autocomplete against the tagged-RID database.
    return (
      <RidSelect
        value={row.values}
        onChange={(value) => updateRow(index, { values: value })}
      />
    );
  };

  return (
    <div className="advanced-filter">
      <Segment style={{ marginBottom: "0.75em" }}>
        <Header as="h5" style={{ marginBottom: "0.5em" }}>Timeframe (optional)</Header>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1em", alignItems: "center" }}>
          <div>
            <span style={{ marginRight: "0.5em" }}>From</span>
            <DatePicker
              selected={tf.from}
              onChange={(date) => onTimeframeChange({ ...tf, from: date })}
              selectsStart
              startDate={tf.from}
              endDate={tf.to}
              maxDate={tf.to || new Date()}
              minDate={minDate}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="LLL"
              isClearable
              placeholderText="Start"
            />
          </div>
          <div>
            <span style={{ marginRight: "0.5em" }}>To</span>
            <DatePicker
              selected={tf.to}
              onChange={(date) => onTimeframeChange({ ...tf, to: date })}
              selectsEnd
              startDate={tf.from}
              endDate={tf.to}
              maxDate={new Date()}
              minDate={tf.from || minDate}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="LLL"
              isClearable
              placeholderText="End (now)"
            />
          </div>
        </div>
      </Segment>

      {rows.length === 0 && (
        <p style={{ color: "#888" }}>
          No conditions yet. Add one to start building a filter.
        </p>
      )}

      {rows.map((row, index) => {
        const opOptions = BOOLEAN_FIELDS.includes(row.field) ? OP_OPTIONS_BOOL : OP_OPTIONS_SET;
        return (
          <Segment key={index} style={{ marginBottom: "0.75em" }}>
            {index > 0 && (
              <div style={{ textAlign: "center", marginBottom: "0.5em" }}>
                <Dropdown
                  compact selection
                  options={JOIN_OPTIONS}
                  value={row.join === "OR" ? "OR" : "AND"}
                  onChange={(e, { value }) => updateRow(index, { join: value })}
                />
              </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5em", alignItems: "flex-start" }}>
              <Dropdown
                selection
                options={FIELD_OPTIONS}
                value={row.field}
                style={{ minWidth: "10em" }}
                onChange={(e, { value }) => handleFieldChange(index, value)}
              />
              <Dropdown
                selection compact
                options={opOptions}
                value={row.op === "notIn" ? "notIn" : "in"}
                onChange={(e, { value }) => updateRow(index, { op: value })}
              />
              <div style={{ flex: "1 1 12em", minWidth: "10em" }}>
                {renderValueControl(row, index)}
              </div>
              <Button icon basic color="red" onClick={() => removeRow(index)} title="Remove condition">
                <Icon name="trash" />
              </Button>
            </div>
          </Segment>
        );
      })}

      <Button basic onClick={addRow} style={{ marginTop: "0.25em" }}>
        <Icon name="plus" /> Add condition
      </Button>

      {rows.length > 1 && (
        <Label basic pointing="left" style={{ marginLeft: "0.75em" }}>
          AND binds tighter than OR
        </Label>
      )}
    </div>
  );
}

export default AdvancedFilter;
